import React, { useState, useEffect, useRef } from 'react';

export default function MeetingPage() {
  const params = new URLSearchParams(window.location.search);
  const room = params.get('room');
  const name = params.get('name') || 'Participant';
  const role = params.get('role') || 'guest';
  const candidateId = params.get('cid');

  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const jitsiRef = useRef(null);
  const apiRef = useRef(null);

  const displayName = role === 'admin' ? 'Skillora Admin' : role === 'client' ? 'Skillora Client' : role === 'vendor' ? 'Skillora Vendor' : 'Skillora Participant';

  const startMeeting = () => {
    setStarted(true);
    setTimeout(() => {
      if (!window.JitsiMeetExternalAPI) return;
      const domain = 'meet.jit.si';
      const options = {
        roomName: room || 'Skillora-Interview-' + Date.now(),
        width: '100%',
        height: '100%',
        parentNode: jitsiRef.current,
        userInfo: { displayName, email: '' },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          disableDeepLinking: true,
          enableRecording: true,
          localRecording: { enabled: true, format: 'flac' },
          toolbarButtons: ['microphone', 'camera', 'desktop', 'fullscreen', 'fodeviceselection', 'hangup', 'chat', 'recording', 'livestreaming', 'etherpad', 'settings', 'raisehand', 'videoquality', 'filmstrip', 'participants-pane', 'tileview'],
          prejoinPageEnabled: false,
          disableThirdPartyRequests: false,
          analytics: { disabled: true },
        },
        interfaceConfigOverwrite: {
          APP_NAME: 'Skillora Interview',
          NATIVE_APP_NAME: 'Skillora',
          DEFAULT_BACKGROUND: '#1e1b4b',
          DEFAULT_LOCAL_DISPLAY_NAME: displayName,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          BRAND_WATERMARK_LINK: '',
          SHOW_POWERED_BY: false,
          DISPLAY_WELCOME_PAGE_CONTENT: false,
          TOOLBAR_ALWAYS_VISIBLE: false,
          MOBILE_APP_PROMO: false,
          HIDE_INVITE_MORE_HEADER: true,
          RECENT_LIST_ENABLED: false,
          SETTINGS_SECTIONS: ['devices', 'language'],
        },
      };
      apiRef.current = new window.JitsiMeetExternalAPI(domain, options);

      // Auto start local recording
      apiRef.current.addEventListener('videoConferenceJoined', () => {
        setTimeout(() => {
          try {
            apiRef.current.executeCommand('localRecording', { onlySelf: false });
          } catch(e) { console.log('Recording auto-start:', e); }
        }, 2000);
      });

      // Prevent recording stop - re-start if stopped
      apiRef.current.addEventListener('localRecordingStatusChanged', (event) => {
        if (event && event.on === false) {
          setTimeout(() => {
            try {
              apiRef.current.executeCommand('localRecording', { onlySelf: false });
            } catch(e) {}
          }, 1000);
        }
      });
      apiRef.current.addEventListener('videoConferenceLeft', () => setEnded(true));
    }, 500);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    document.head.appendChild(script);
    return () => { if (apiRef.current) apiRef.current.dispose(); };
  }, []);

  if (ended) return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system,sans-serif' }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', maxWidth: '480px', width: '100%' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
        <h2 style={{ color: '#1e293b', fontSize: '24px', fontWeight: '700', margin: '0 0 8px' }}>Interview Completed!</h2>
        <p style={{ color: '#64748b', margin: '0 0 24px', fontSize: '14px' }}>The interview session has ended. Thank you for using Skillora.</p>
        <button onClick={() => window.close()} style={{ padding: '12px 28px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' }}>Close Window</button>
      </div>
    </div>
  );

  if (!started) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0f172a,#1e1b4b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system,sans-serif', padding: '20px' }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', maxWidth: '520px', width: '100%', boxShadow: '0 40px 80px rgba(0,0,0,0.4)' }}>
        <div style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '28px' }}>⚡</div>
        <h2 style={{ color: '#1e293b', fontSize: '24px', fontWeight: '800', margin: '0 0 8px' }}>Skillora Interview Room</h2>
        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 28px' }}>You are about to join a secure interview session powered by Skillora.</p>

        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'left', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ color: '#64748b', fontSize: '13px' }}>Your Name</span>
            <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '13px' }}>{displayName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ color: '#64748b', fontSize: '13px' }}>Room</span>
            <span style={{ color: '#6366f1', fontWeight: '600', fontSize: '12px' }}>{room?.slice(0, 20)}...</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#64748b', fontSize: '13px' }}>Security</span>
            <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '13px' }}>🔒 Encrypted</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <div style={{ flex: 1, background: '#f0fdf4', borderRadius: '10px', padding: '14px', border: '1px solid #86efac', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', marginBottom: '4px' }}>🎥</div>
            <div style={{ color: '#16a34a', fontSize: '12px', fontWeight: '600' }}>Camera Ready</div>
          </div>
          <div style={{ flex: 1, background: '#f0fdf4', borderRadius: '10px', padding: '14px', border: '1px solid #86efac', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', marginBottom: '4px' }}>🎤</div>
            <div style={{ color: '#16a34a', fontSize: '12px', fontWeight: '600' }}>Mic Ready</div>
          </div>
          <div style={{ flex: 1, background: '#ede9fe', borderRadius: '10px', padding: '14px', border: '1px solid #c4b5fd', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', marginBottom: '4px' }}>⏺️</div>
            <div style={{ color: '#6366f1', fontSize: '12px', fontWeight: '600' }}>Recording On</div>
          </div>
        </div>

        <button onClick={startMeeting} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '16px', fontWeight: '700', fontFamily: 'inherit', boxShadow: '0 0 30px rgba(99,102,241,0.3)' }}>
          🚀 Join Interview Now
        </button>
        <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '12px' }}>By joining, you agree to Skillora's recording policy</p>
      </div>
    </div>
  );

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>⚡</span>
          <span style={{ color: 'white', fontWeight: '800', fontSize: '15px' }}>Skillora Interview</span>
          <span style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>🔴 LIVE</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>{displayName}</span>
          <span style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '2px 8px', borderRadius: '6px', fontSize: '11px' }}>⏺️ Recording</span>
        </div>
      </div>
      <div ref={jitsiRef} style={{ flex: 1, width: '100%' }} />
    </div>
  );
}
